import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { Alert, AlertTitle, Box, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { config, describeDataOrigin } from '@/config/env';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { NotaPrzyczynowosci } from '@/components/NotaPrzyczynowosci';
import { PasSekcji } from '@/components/PasSekcji';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { DataQualityBadge, ErrorState, InsightOutcome, LoadingState } from '@/components/states';
import { formatCount, formatDelta, formatGrosze, formatRange } from '@/lib/format';
import { useAuth } from '@/lib/auth/AuthContext';
import { useSalesDeliveryCoincidence, useSchoolSales } from '@/lib/useAnalytics';
import { useDateRange, type DateRange } from '@/lib/useDateRange';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import {
  CROSS_ENGINE_VERSION,
  powodBrakuPorownania,
  DEFAULT_THRESHOLDS,
  type Coincidence,
  type JoinReport,
  type JoinStatus,
} from '@/lib/crossSystem';
import { klasyfikacjaDrive, swiezoscDrive } from '@/lib/pochodzenieDanych';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * POWIĄZANIA — jedyny ekran, na którym widać, PO CO w ogóle jest Control Center
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Sprzedaż siedzi w CRM, punktualność w aplikacji kierowców. Osobno obie liczby są dostępne od
 * dawna i osobno obie są bezużyteczne do tego jednego pytania, które właściciel naprawdę zadaje:
 * „czy to, że wozimy gorzej, widać w sprzedaży?”. Ten ekran stawia obie strony obok siebie —
 * i konsekwentnie ODMAWIA odpowiedzi na pytanie o przyczynę, bo dane na nie nie odpowiadają.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ DLACZEGO LICZBĄ-BOHATEREM JEST POKRYCIE ZŁĄCZENIA, A NIE LICZBA ZNALEZIONYCH ZBIEŻNOŚCI. │
 * │ Wielkie „3” pod napisem „szkoły, w których spadła sprzedaż i punktualność” zostanie       │
 * │ przeczytane jako „w trzech szkołach dostawy zjadły nam sprzedaż” i w tej postaci trafi    │
 * │ na slajd. Pokrycie złączenia takiego drugiego życia nie ma: mówi wyłącznie, ILE Z DWÓCH   │
 * │ LIST DA SIĘ W OGÓLE ZESTAWIĆ — i jest jedyną liczbą, od której zależy, czy reszta ekranu  │
 * │ cokolwiek znaczy. Gdy jest niska albo nieznana, wszystko poniżej jest ciekawostką.        │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH. Ekran czyta wyłącznie realne API przez istniejące haki.
 * Puste źródło pokazujemy jako pustkę — z podanym oknem, którego ta pustka dotyczy.
 */

// ── drobne narzędzia prezentacji (żadnych nowych liczb, tylko ich zapis) ──────────────────────

/** Zmiana w punktach procentowych. `null` znaczy „nie da się policzyć”, nigdy „0 p.p.”. */
function formatPunkty(punkty: number | null | undefined): string {
  if (punkty === null || punkty === undefined || !Number.isFinite(punkty)) return 'brak danych';
  const znak = punkty > 0 ? '+' : '';
  return `${znak}${punkty.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} p.p.`;
}

/** Udział w procentach. `null` to brak wyniku, a nie zero procent. */
function formatUdzial(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return 'brak danych';
  return `${pct.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%`;
}

function formatMinuty(minuty: number | null | undefined): string {
  if (minuty === null || minuty === undefined || !Number.isFinite(minuty)) return 'brak danych';
  const znak = minuty > 0 ? '+' : '';
  return `${znak}${minuty.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} min`;
}

const BARWA_STATUSU: Record<JoinStatus, string> = {
  READY: 'var(--sukces)',
  PARTIAL: 'var(--uwaga)',
  BLOCKED: 'var(--krytyczny)',
};

const TLO_STATUSU: Record<JoinStatus, string> = {
  READY: 'var(--sukces-tlo)',
  PARTIAL: 'var(--uwaga-tlo)',
  BLOCKED: 'var(--krytyczny-tlo)',
};

function Znacznik({
  tekst,
  barwa,
  tlo,
  kartaId,
}: {
  tekst: string;
  barwa: string;
  tlo: string;
  /** Stabilny adres karty dla walidacji DOM (R5-15). Sam atrybut, zero wpływu na wygląd. */
  kartaId?: string;
}) {
  return (
    <Box
      component="span"
      data-cc-card-id={kartaId}
      sx={{
        px: 1.25,
        py: 0.25,
        borderRadius: 'var(--r-pigulka)',
        background: tlo,
        color: barwa,
        border: `1px solid ${barwa}`,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {tekst}
    </Box>
  );
}

/** Wiersz „etykieta → wartość”. Wartość zawsze cyframi o stałej szerokości. */
function Wiersz({
  etykieta,
  wartosc,
  dopisek,
  kartaId,
}: {
  etykieta: string;
  wartosc: string;
  dopisek?: ReactNode;
  /** Stabilny adres karty dla walidacji DOM (R5-15). Sam atrybut, zero wpływu na wygląd. */
  kartaId?: string;
}) {
  return (
    <Box data-cc-card-id={kartaId} sx={{ py: 1, borderBottom: '1px dashed var(--linia)' }}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>
          {etykieta}
        </Typography>
        <Typography variant="body2" className="sygnal-liczba" sx={{ fontWeight: 700, textAlign: 'right' }}>
          {wartosc}
        </Typography>
      </Stack>
      {dopisek ? (
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 0.25 }}>
          {dopisek}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Nagłówek kolumny jednego systemu — na tym ekranie strona zawsze ma podpisane źródło. */
function GlowicaSystemu({ system, opis, znacznik }: { system: string; opis: string; znacznik: ReactNode }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="overline" sx={{ color: 'var(--tekst)' }}>
          {system}
        </Typography>
        {znacznik}
      </Stack>
      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
        {opis}
      </Typography>
    </Box>
  );
}

// ── karta jednej szkoły: sprzedaż obok punktualności ──────────────────────────────────────────

/**
 * Jedna szkoła, dwie niezależnie policzone zmiany, zero strzałki między nimi.
 *
 * Strzałka „sprzedaż ← dostawy” byłaby tu najtańszym możliwym kłamstwem graficznym: nic w danych
 * nie wskazuje kierunku. Kolumny stoją więc obok siebie, rozdzielone kreską, a nie połączone.
 *
 * PRÓBA I POKRYCIE STOJĄ PRZY KAŻDEJ LICZBIE. Nie w przypisie na dole ekranu — przypis czyta
 * ten, kto już podjął decyzję.
 */
function KartaPowiazania({
  item,
  join,
  okno,
  porownanie,
}: {
  item: Coincidence;
  join: JoinReport;
  okno: DateRange;
  porownanie: DateRange;
}) {
  const nazwa = item.school_name ?? `Szkoła #${item.school_id}`;
  const oknoTekst = `${okno.date_from} → ${okno.date_to}`;
  const porownanieTekst = `${porownanie.date_from} → ${porownanie.date_to}`;
  const pokrycieTekst = `pokrycie złączenia ${formatUdzial(join.match_pct)} · ${formatCount(join.matched)} dopasowanych szkół`;

  const dowodSprzedazy: Dowod = {
    tytul: `Zmiana sprzedaży — ${nazwa}`,
    etykietaWartosci: 'Zmiana wartości wg cennika',
    wartosc: item.sales_delta_pct === null ? null : formatDelta(item.sales_delta_pct),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales → dwa okna tej samej długości, szkoła z transakcji',
    // Świeżość strony sprzedażowej jest w całym programie NIEUSTALONA — stempel odczytu byłby zmyślony.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: oknoTekst,
    oknoPorownawcze: porownanieTekst,
    wyliczenie: '(wartość w oknie bieżącym − wartość w oknie porównawczym) ÷ wartość w oknie porównawczym',
    szczegoly: [
      ['Wartość teraz', formatGrosze(item.sales_current_grosze)],
      ['Wartość wcześniej', formatGrosze(item.sales_previous_grosze)],
      ['Zamówienia teraz', formatCount(item.orders_current)],
      ['Zamówienia wcześniej', formatCount(item.orders_previous)],
      ['Próg wejścia na listę', `spadek co najmniej ${DEFAULT_THRESHOLDS.minSalesDropPct}%`],
      ['Silnik', CROSS_ENGINE_VERSION],
    ],
    proba: `${formatCount(item.orders_current)} zamówień w oknie bieżącym`,
    // NIE jest to próg wejścia: wiersz poniżej minimum NIE znika z listy, tylko dostaje niższą
    // jakość danych. Napisanie tu samego „20 zamówień” sugerowałoby filtr, którego nie ma.
    wymaganeMinimum: `${formatCount(DEFAULT_THRESHOLDS.minOrders)} zamówień — próg OSTROŻNOŚCI, nie filtr: poniżej niego wiersz zostaje na liście z obniżoną jakością danych`,
    jakoscDanych: item.data_quality_reason,
    ograniczenia: [
      'Wartość wg cennika liczy ceny przed zniżką szkolną — to nie jest kwota pobrana.',
      `Złączenie: ${join.reason}`,
    ],
    czegoNieWynika:
      'Z tej zmiany nie wynika, ile pieniędzy realnie ubyło — liczone są ceny katalogowe, a nie kwoty pobrane, a te dwie wielkości rozjeżdżają się nierównomiernie (szkoły z promocją inaczej niż bez). Nie wynika też, że kupowało mniej osób: liczby różnych kupujących na tym ziarnie nie ma, jest wyłącznie liczba zamówień, więc spadek średniej wartości zamówienia wygląda tu identycznie jak odejście uczniów.',
  };

  const dowodPunktualnosci: Dowod = {
    tytul: `Zmiana punktualności — ${nazwa}`,
    etykietaWartosci: 'Zmiana udziału dostaw na czas',
    wartosc: item.on_time_delta_points === null ? null : formatPunkty(item.on_time_delta_points),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-by-school → status utrwalony w chwili dostawy',
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: swiezoscDrive(),
    oknoBiezace: oknoTekst,
    oknoPorownawcze: porownanieTekst,
    wyliczenie: 'udział dostaw w oknie tolerancji: okno bieżące − okno porównawcze, w punktach procentowych',
    szczegoly: [
      ['Punktualność teraz', formatUdzial(item.on_time_current_pct)],
      ['Punktualność wcześniej', formatUdzial(item.on_time_previous_pct)],
      ['Średnie odchylenie teraz', formatMinuty(item.avg_delta_current_min)],
      ['Zmiana średniego odchylenia', formatMinuty(item.avg_delta_change_min)],
      ['Dostawy w oknie', formatCount(item.deliveries)],
      ['Próg wejścia na listę', `pogorszenie co najmniej ${DEFAULT_THRESHOLDS.minOnTimeDropPoints} p.p.`],
    ],
    proba: `${formatCount(item.deliveries)} dostaw w oknie bieżącym`,
    wymaganeMinimum: `${formatCount(DEFAULT_THRESHOLDS.minDeliveries)} dostaw — próg OSTROŻNOŚCI, nie filtr: poniżej niego wiersz zostaje na liście z obniżoną jakością danych`,
    jakoscDanych: item.data_quality_reason,
    ograniczenia: [
      'Dostawa przed czasem nie jest liczona jako punktualna i nie jest liczona jako spóźnienie.',
      `Złączenie: ${join.reason}`,
    ],
    czegoNieWynika:
      'Z tej liczby nie wynika przyczyna spóźnień: na ziarnie szkoły zbiór nie zawiera ani kierowcy, ani trasy, ani restauracji, która danego dnia wozi. Nie wynika też, że uczniowie to zauważyli — punktualność mierzymy względem zapisanej godziny, a nie względem tego, o której faktycznie zaczyna się przerwa.',
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 'var(--r-karta)' }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }} useFlexGap>
        <Typography variant="h6" sx={{ minWidth: 0 }}>
          {nazwa}
        </Typography>
        {item.city ? (
          <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>
            {item.city}
          </Typography>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {/*
          Odznaka jakości W NAGŁÓWKU karty szkoły jest osobną kartą inwentarza (CROSS_SYSTEM-22),
          więc adres siedzi na niej, a nie na obejmującym ją Paperze. Powtarza się raz na szkołę —
          dokładnie tak jak sąsiednie CROSS_SYSTEM-18…21 wewnątrz tej samej karty: identyfikator
          opisuje MIEJSCE w karcie, a rozróżnienie szkół niesie już nazwa w nagłówku.
        */}
        <DataQualityBadge
          kartaId="CROSS_SYSTEM-22"
          quality={item.data_quality}
          reason={item.data_quality_reason}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2.5, md: 4 },
          gridTemplateColumns: { xs: '1fr', md: '1fr 1px 1fr' },
          alignItems: 'stretch',
        }}
      >
        {/* ── strona sprzedażowa ─────────────────────────────────────────────────────────── */}
        <Box>
          <GlowicaSystemu
            system="Sprzedaż · CRM"
            opis="ceny katalogowe pozycji zamówienia, przed zniżką szkolną"
            znacznik={<Znacznik tekst="świeżość nieustalona" barwa="var(--uwaga)" tlo="var(--uwaga-tlo)" />}
          />

          <Typography
            className="sygnal-liczba"
            data-cc-card-id="CROSS_SYSTEM-18"
            sx={{
              fontFamily: 'var(--krój-display)',
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              lineHeight: 1,
              color: (item.sales_delta_pct ?? 0) < 0 ? 'var(--krytyczny)' : 'var(--tekst)',
              my: 1,
            }}
          >
            {item.sales_delta_pct === null ? 'brak danych' : formatDelta(item.sales_delta_pct)}
          </Typography>

          <Typography
            variant="body2"
            className="sygnal-liczba"
            data-cc-card-id="CROSS_SYSTEM-19"
            sx={{ color: 'var(--tekst-2)' }}
          >
            {formatGrosze(item.sales_previous_grosze)} → {formatGrosze(item.sales_current_grosze)}
          </Typography>

          <Box sx={{ mt: 1.5, mb: 1 }}>
            <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />
          </Box>

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
            próba: {formatCount(item.orders_current)} zamówień teraz / {formatCount(item.orders_previous)} wcześniej ·
            próg ostrożności {formatCount(DEFAULT_THRESHOLDS.minOrders)} zamówień
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
            {pokrycieTekst}
          </Typography>

          <Box sx={{ ml: -1, mt: 0.5 }}>
            <PrzyciskDowodu dowod={dowodSprzedazy} etykieta="Skąd ta zmiana sprzedaży" />
          </Box>
        </Box>

        {/* Kreska, nie strzałka. Kierunku zależności dane nie znają, więc grafika też go nie zna. */}
        <Box
          aria-hidden
          sx={{ display: { xs: 'none', md: 'block' }, width: '1px', background: 'var(--linia-mocna)' }}
        />

        {/* ── strona dostawowa ───────────────────────────────────────────────────────────── */}
        <Box>
          <GlowicaSystemu
            system="Dostawy · Drive"
            opis="status utrwalony w chwili dostawy, nie przeliczany wstecz"
            znacznik={<Znacznik tekst="zbiór kontrolowany" barwa="var(--neutralny)" tlo="var(--neutralny-tlo)" />}
          />

          <Typography
            className="sygnal-liczba"
            data-cc-card-id="CROSS_SYSTEM-20"
            sx={{
              fontFamily: 'var(--krój-display)',
              fontSize: 'clamp(28px, 3.4vw, 44px)',
              lineHeight: 1,
              color: (item.on_time_delta_points ?? 0) < 0 ? 'var(--krytyczny)' : 'var(--tekst)',
              my: 1,
            }}
          >
            {item.on_time_delta_points === null ? 'brak danych' : formatPunkty(item.on_time_delta_points)}
          </Typography>

          <Typography
            variant="body2"
            className="sygnal-liczba"
            data-cc-card-id="CROSS_SYSTEM-21"
            sx={{ color: 'var(--tekst-2)' }}
          >
            {formatUdzial(item.on_time_previous_pct)} → {formatUdzial(item.on_time_current_pct)} dostaw na czas
          </Typography>

          <Typography variant="body2" className="sygnal-liczba" sx={{ color: 'var(--tekst-2)' }}>
            średnie odchylenie: {formatMinuty(item.avg_delta_current_min)} (zmiana {formatMinuty(item.avg_delta_change_min)})
          </Typography>

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1.5 }}>
            próba: {formatCount(item.deliveries)} dostaw · próg ostrożności {formatCount(DEFAULT_THRESHOLDS.minDeliveries)} dostaw
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
            {pokrycieTekst}
          </Typography>

          <Box sx={{ ml: -1, mt: 0.5 }}>
            <PrzyciskDowodu dowod={dowodPunktualnosci} etykieta="Skąd ta zmiana punktualności" />
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
        Obie zmiany dotyczą tego samego okna {oknoTekst}, porównanego z {porownanieTekst}. Współwystępowanie,
        nie przyczyna — kolumny stoją obok siebie, bo dane nie wskazują, która jest po której stronie.
      </Typography>
    </Paper>
  );
}

// ── ekran ─────────────────────────────────────────────────────────────────────────────────────

export function PowiazaniaPage() {
  const auth = useAuth();
  const [range] = useDateRange();
  const oknoTekst = `${range.date_from} → ${range.date_to}`;

  const cross = useSalesDeliveryCoincidence();
  /*
   * Ta sama para (klucz, parametry) co zapytanie w środku haka zbieżności, więc React Query oddaje
   * TEN SAM wpis cache — żadnego dodatkowego wywołania sieci. Potrzebujemy jej, bo hak zwraca sam
   * wynik złączenia i nie mówi, czy strona sprzedażowa była PUSTA, czy tylko nie miała części
   * wspólnej z dostawami. To rozróżnienie jest na tym ekranie kluczowe.
   */
  const sales = useSchoolSales();

  const driveConnected = czyWidziDostawy(auth);
  const loading = cross.isLoading || sales.isLoading;
  const error = cross.error ?? sales.error;

  const crossReady = cross.data?.available === true ? cross.data : null;
  const join = crossReady?.join ?? null;

  /*
   * ZERO TO NIE TO SAMO CO PUSTO (lib/pustyZbior.ts). Suma z pustego zbioru wynosi 0, więc CRM
   * zwraca zero także wtedy, gdy nie było ANI JEDNEGO wiersza do zsumowania. Na tym ekranie
   * pomyłka byłaby szczególnie kosztowna: „sprzedaż spadła do zera przy gorszych dostawach” to
   * zupełnie inna historia niż „nie mamy danych sprzedażowych”.
   */
  const ziarnoSprzedazy = {
    wierszy: sales.data?.summary.total_orders ?? null,
    bytow: sales.data?.schools.length ?? null,
  };
  const sprzedazPusta = czyPustyZbior(ziarnoSprzedazy);
  const wartoscSprzedazy = wartoscDoPokazania(sales.data?.summary.total_sales_grosze, ziarnoSprzedazy);

  const powodZHaka = cross.data?.available === false ? cross.data.reason : null;
  const brakWyniku = !crossReady && powodZHaka === null;
  const zablokowane = !crossReady;

  const pokrycieWartosc = join?.match_pct ?? null;
  const pokrycieTekst = pokrycieWartosc === null ? null : formatUdzial(pokrycieWartosc);

  /*
   * Dowód jest budowany z TYCH SAMYCH pól, które zasilają liczbę na ekranie. Warstwa prezentacji
   * nie jest właścicielem liczb — gdyby była, pierwsza zmiana definicji rozjechałaby ekran z jego
   * własnym wyprowadzeniem i nie dałoby się stwierdzić, które z dwojga kłamie.
   */
  const dowodPokrycia: Dowod = {
    tytul: 'Pokrycie złączenia sprzedaż ↔ dostawy',
    etykietaWartosci: 'Udział dopasowanych szkół',
    wartosc: pokrycieTekst,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales × Drive /analytics/delivery-by-school → klucz tuszama_id',
    // Złączenie jest tak świeże jak jego najsłabsza strona, a strona sprzedażowa nie podaje
    // momentu odczytu. Wpisanie tu „bieżąca” zawyżałoby wiarygodność całego ekranu.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — strona sprzedażowa nie raportuje momentu odczytu',
    oknoBiezace: oknoTekst,
    oknoPorownawcze: crossReady ? `${crossReady.comparison.date_from} → ${crossReady.comparison.date_to}` : null,
    wyliczenie: 'dopasowane identyfikatory szkół ÷ liczniejszy z dwóch zbiorów (sprzedaż, dostawy)',
    szczegoly: [
      ['Status złączenia', join?.status ?? 'nieocenione'],
      ['Szkoły ze sprzedażą', formatCount(join?.sales_schools ?? null)],
      ['Szkoły z dostawami', formatCount(join?.delivery_schools ?? null)],
      ['Dopasowane', formatCount(join?.matched ?? null)],
      ['Tylko sprzedaż', formatCount(join?.sales_only ?? null)],
      ['Tylko dostawy', formatCount(join?.delivery_only ?? null)],
      ['Dostawy bez identyfikatora szkoły', formatCount(join?.delivery_rows_without_id ?? null)],
      ['Klucz złączenia', 'tuszama_id — BIGINT po stronie CRM, INTEGER po stronie Drive'],
      ['Silnik', CROSS_ENGINE_VERSION],
    ],
    proba: join ? `${formatCount(join.matched)} dopasowanych szkół` : 'złączenia nie policzono',
    jakoscDanych: join?.reason,
    ograniczenia: [
      ...(join ? [join.reason] : ['Złączenia nie policzono — brak jednej ze stron.']),
      ...(sprzedazPusta
        ? [
            'STRONA SPRZEDAŻOWA JEST PUSTA: zero zamówień i zero szkół w tym oknie. Brak części ' +
              'wspólnej NIE oznacza wtedy, że szkoły są rozłączne — oznacza, że jednej listy w ogóle nie ma.',
          ]
        : []),
      'Szkoła obecna po jednej stronie nie jest błędem: mogła nie mieć w tym oknie dostaw albo nie mieć sprzedaży.',
    ],
    czegoNieWynika:
      'Z pokrycia nie wynika, że dopasowane rekordy opisują te same zdarzenia — to zgodność identyfikatora SZKOŁY, a nie zgodność dostaw z zamówieniami. Nie wynika też, że część niedopasowana jest usterką integracji: dopóki nie wiadomo, ile szkół powinno być po obu stronach, ta liczba mierzy wyłącznie przecięcie dwóch list, a nie kompletność żadnej z nich.',
  };

  const naglowekBlokady = sprzedazPusta ? 'Brak sprzedaży w tym oknie' : 'Brak danych do zestawienia';

  return (
    <Box>
      <NaglowekEkranu
        tytul="Powiązania"
        opis="Ekran odpowiada na jedno pytanie: czy w tych samych szkołach i w tym samym oknie spadła sprzedaż z CRM i pogorszyła się punktualność z aplikacji kierowców. Odpowiada na nie zestawieniem dwóch niezależnie policzonych stron — i nie idzie ani kroku dalej w stronę przyczyny."
        akcja={<DateRangeFilter wariant="naglowek" comparison={crossReady?.comparison} />}
      />

      {/*
        MANIFEST PRZYCZYNOWOŚCI NAD EKRANEM — odpowiednik `.pw-manifest` z makiety.

        Ten ekran zestawia dwa niezależne systemy, więc jest w całym produkcie miejscem,
        w którym najłatwiej wyciągnąć wniosek o przyczynie. Zdanie „Związek przyczynowy
        nieustalony" stało dotąd DOPIERO PRZY WYNIKU, czyli po zestawieniu — a czytelnik,
        który zobaczy dwie kolumny spadające razem, wyciąga wniosek natychmiast.
        Zastrzeżenie przy wyniku ZOSTAJE; to dwie różne role.
      */}
      <NotaPrzyczynowosci tytul="Przyczynowość: nieustalona" kartaId="CROSS_SYSTEM-15">
        Zestawienie pokazuje WSPÓŁWYSTĘPOWANIE: te same szkoły, to samo okno, dwie wielkości
        policzone niezależnie po dwóch stronach. Nie pokazuje, czy jedna wywołała drugą, ani czy
        obie nie wynikają z trzeciej, której nie mierzymy — zbiór na ziarnie szkoły nie zawiera
        ani kierowcy, ani trasy, ani restauracji wożącej danego dnia. To nie jest też korelacja
        w sensie statystycznym: nie liczymy współczynnika i nie badamy istotności.
      </NotaPrzyczynowosci>

      {loading ? <LoadingState label="Łączenie danych sprzedażowych z dostawami…" /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy jeden z dwóch backendów nie odpowiada, nie pokazujemy złączenia z jedną stroną — pokazujemy błąd."
        />
      ) : null}

      {!loading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          {/* TRZY DZIAŁY: na czym stoi zestawienie -> co wyszło -> czego nie wiadomo.
              Ekran nie mial ANI JEDNEGO pasa sekcji, a prowadzi przez trzy rozne rodzaje
              tresci: pomiar zlaczenia, wynik zestawienia i granice wniosku. */}
          <PasSekcji wariant="cichy" tytul="Na czym stoi zestawienie" />

          {/* ── liczba-bohater: czy te dwa systemy w ogóle da się zestawić ───────────────── */}
          <PlytaGlowna
            kartaId="CROSS_SYSTEM-01"
            etykieta="Pokrycie złączenia sprzedaż ↔ dostawy"
            wartosc={pokrycieTekst}
            opis={
              <Typography variant="body2">
                {pokrycieTekst === null
                  ? 'złączenia nie policzono — dopóki nie ma obu stron, nie ma czego z czym zestawiać'
                  : 'udział szkół dopasowanych po identyfikatorze systemu głównego — to nie jest miara sprzedaży ani punktualności, tylko tego, ile z obu list da się w ogóle zestawić'}
              </Typography>
            }
            zrodlo="CRM /analytics/school-sales × Drive /analytics/delivery-by-school (klucz tuszama_id)"
            okno={formatRange(range.date_from, range.date_to)}
            dowod={dowodPokrycia}
            boczne={
              <>
                <MiniMetryka
                  kartaId="CROSS_SYSTEM-02"
                  wartosc={join ? formatCount(join.matched) : null}
                  etykieta="szkół dopasowanych"
                />
                <MiniMetryka
                  kartaId="CROSS_SYSTEM-03"
                  wartosc={join ? formatCount(join.sales_only) : null}
                  etykieta="tylko sprzedaż"
                />
                <MiniMetryka
                  kartaId="CROSS_SYSTEM-04"
                  wartosc={join ? formatCount(join.delivery_only) : null}
                  etykieta="tylko dostawy"
                />
                <MiniMetryka
                  kartaId="CROSS_SYSTEM-05"
                  wartosc={join ? formatCount(join.delivery_rows_without_id) : null}
                  etykieta="dostaw bez ID szkoły"
                />
              </>
            }
          />

          {/* ── wiarygodność złączenia: dwie strony obok siebie, potem ich przecięcie ────── */}
          <Box>
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Na czym stoi każda liczba niżej
            </Typography>
            <Typography variant="h5" sx={{ mb: 1.5 }}>
              Wiarygodność złączenia
            </Typography>

            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 'var(--r-karta)' }}>
              <Box sx={{ display: 'grid', gap: { xs: 2.5, md: 4 }, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <Box>
                  <GlowicaSystemu
                    system="Strona sprzedaży · CRM"
                    opis="agregat po szkołach z transakcji, oś czasu: dzień dostawy"
                    znacznik={<Znacznik tekst="świeżość nieustalona" barwa="var(--uwaga)" tlo="var(--uwaga-tlo)" />}
                  />
                  <Wiersz
                    kartaId="CROSS_SYSTEM-06"
                    etykieta="Szkoły w odpowiedzi"
                    wartosc={formatCount(sales.data?.schools.length ?? null)}
                  />
                  <Wiersz
                    kartaId="CROSS_SYSTEM-07"
                    etykieta="Zamówienia w oknie"
                    wartosc={formatCount(sales.data?.summary.total_orders ?? null)}
                  />
                  <Wiersz
                    kartaId="CROSS_SYSTEM-08"
                    etykieta="Wartość wg cennika"
                    // Pusty zbiór NIE jest zerem złotych. wartoscDoPokazania() zwraca tu null,
                    // a null renderujemy jako brak danych — nigdy jako 0,00 zł.
                    wartosc={wartoscSprzedazy === null ? 'brak danych' : formatGrosze(wartoscSprzedazy)}
                    dopisek={
                      sprzedazPusta
                        ? 'źródło zwróciło pusty zbiór — to nie jest zmierzone zero'
                        : 'ceny katalogowe, przed zniżką szkolną — to nie jest kwota pobrana'
                    }
                  />
                  <Box sx={{ mt: 1.5 }}>
                    <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />
                  </Box>
                </Box>

                <Box>
                  <GlowicaSystemu
                    system="Strona dostaw · Drive"
                    opis="agregat po szkołach z utrwalonych statusów dostaw"
                    znacznik={
                      driveConnected ? (
                        <Znacznik tekst="zbiór kontrolowany" barwa="var(--neutralny)" tlo="var(--neutralny-tlo)" />
                      ) : (
                        <Znacznik tekst="niepodłączone" barwa="var(--krytyczny)" tlo="var(--krytyczny-tlo)" />
                      )
                    }
                  />
                  <Wiersz
                    kartaId="CROSS_SYSTEM-09"
                    etykieta="Szkoły z dostawami"
                    wartosc={formatCount(join?.delivery_schools ?? null)}
                  />
                  <Wiersz
                    kartaId="CROSS_SYSTEM-10"
                    etykieta="Dostawy bez identyfikatora szkoły"
                    wartosc={formatCount(join?.delivery_rows_without_id ?? null)}
                    dopisek="takie dostawy nie wchodzą do złączenia — nie ma po czym ich dopasować"
                  />
                  {/* Zdanie „Wymaga podłączenia systemu kierowców” pada na tym ekranie DOKŁADNIE RAZ,
                      w sekcji blokady. Powtórzone tutaj byłoby tym samym komunikatem w dwóch
                      miejscach — a czytelnik szukający, co konkretnie jest niepodłączone, ma jedno
                      miejsce, do którego wraca. */}
                  {!driveConnected ? (
                    <Typography variant="body2" sx={{ mt: 1.5, color: 'var(--tekst-2)' }}>
                      Ta strona zestawienia jest dziś niepodłączona — powód i skutek opisuje sekcja blokady
                      niżej. Aplikacja kierowców ma własny katalog kont i własny sekret sesji, więc Control
                      Center nie zaloguje Cię tam automatycznie. <Link to="/drive">Podłącz dostawy</Link>.
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }} useFlexGap>
                <Typography variant="overline" sx={{ color: 'var(--tekst)' }}>
                  Przecięcie obu list
                </Typography>
                <Znacznik
                  kartaId="CROSS_SYSTEM-11"
                  tekst={join?.status ?? 'nieocenione'}
                  barwa={join ? BARWA_STATUSU[join.status] : 'var(--neutralny)'}
                  tlo={join ? TLO_STATUSU[join.status] : 'var(--neutralny-tlo)'}
                />
                <Box sx={{ flexGrow: 1 }} />
                <PrzyciskDowodu dowod={dowodPokrycia} etykieta="Skąd to pokrycie" />
              </Stack>

              <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1 }}>
                {join
                  ? join.reason
                  : 'Złączenia nie policzono — do przecięcia dwóch list potrzebne są obie listy.'}
              </Typography>

              {/*
               * Sprostowanie, bez którego ekran powtórzyłby komunikat nieprawdziwy w tym stanie.
               * Silnik złączenia opisuje brak części wspólnej zdaniem o „rozłącznych szkołach”, co
               * przy PUSTEJ liście sprzedaży twierdzi coś, czego nie zmierzono: żeby zbiory były
               * rozłączne, oba muszą istnieć.
               */}
              {sprzedazPusta ? (
                <Alert
                  severity="warning"
                  icon={false}
                  data-cc-card-id="CROSS_SYSTEM-12"
                  sx={{ borderRadius: 'var(--r-maly)', mb: 1 }}
                >
                  <AlertTitle>Uwaga na wynik złączenia przy pustej sprzedaży</AlertTitle>
                  <Typography variant="body2">
                    Lista sprzedaży jest pusta (0 zamówień w 0 szkołach), więc brak części wspólnej nie
                    znaczy tu, że sprzedaż i dostawy dotyczą rozłącznych szkół — znaczy, że jednej ze stron
                    w ogóle nie ma. To sygnał techniczny, nie wynik biznesowy.
                  </Typography>
                </Alert>
              ) : null}

              <Wiersz
                kartaId="CROSS_SYSTEM-13"
                etykieta="Dopasowane szkoły"
                wartosc={formatCount(join?.matched ?? null)}
              />
              <Wiersz
                kartaId="CROSS_SYSTEM-14"
                etykieta="Pokrycie złączenia"
                wartosc={pokrycieTekst ?? 'brak danych'}
                dopisek="dopasowane ÷ liczniejszy z dwóch zbiorów"
              />
              {/*
               * Inwentarz traktuje OBA wiersze wyłączności jako JEDNĄ kartę (CROSS_SYSTEM-15),
               * więc adres siedzi na obejmującym je bloku — dwa razy ten sam identyfikator
               * przestałby rozstrzygać się do jednego miejsca, a o to w tym adresie chodzi.
               * Blok jest przezroczysty: żadnego stylu, żadnej ramki, żadnego odstępu.
               */}
              <Box data-cc-card-id="CROSS_SYSTEM-15">
                <Wiersz etykieta="Tylko po stronie sprzedaży" wartosc={formatCount(join?.sales_only ?? null)} />
                <Wiersz etykieta="Tylko po stronie dostaw" wartosc={formatCount(join?.delivery_only ?? null)} />
              </Box>
            </Paper>
          </Box>

          {/* ── ostrzeżenie, którego nie da się przeoczyć ─────────────────────────────────── */}
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 'var(--r-duzy)',
              borderColor: 'var(--uwaga)',
              borderWidth: 2,
              background: 'var(--uwaga-tlo)',
            }}
          >
            <Typography variant="overline" sx={{ color: 'var(--uwaga)' }}>
              Dotyczy każdej liczby na tym ekranie
            </Typography>

            <Typography
              sx={{
                fontFamily: 'var(--krój-display)',
                fontSize: 'clamp(28px, 4.6vw, 56px)',
                lineHeight: 1.02,
                color: 'var(--atrament)',
                my: 1.5,
              }}
            >
              PRZYCZYNOWOŚĆ NIEUSTALONA
            </Typography>

            <Typography variant="body1" sx={{ maxWidth: '80ch', color: 'var(--tekst)' }}>
              Związek przyczynowy nieustalony. Ten ekran pokazuje, że dwie wielkości zmieniły się w tym
              samym oknie i w tej samej szkole. To wszystko, co dane mówią — i celowo nie ma tu ani jednej
              liczby, która wyglądałaby na miarę siły tego związku.
            </Typography>

            <Divider sx={{ my: 3, borderColor: 'var(--uwaga)' }} />

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'var(--uwaga)' }}>
                  Co dane mówią
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tekst)' }}>
                  Dwie niezależnie policzone zmiany wypadły w tym samym oknie czasowym, w szkole
                  dopasowanej po identyfikatorze. Jedna pochodzi z CRM, druga z aplikacji kierowców.
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ color: 'var(--uwaga)' }}>
                  Czego nie rozstrzygnie więcej tych samych danych
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tekst)' }}>
                  Kierunek może być odwrotny: mniej zamówień to inna trasa i inne godziny. Przyczyna może
                  być wspólna i w tym zbiorze niewidoczna — kalendarz szkoły nie jest tu żadną kolumną.
                  Zbieżność może też być przypadkiem: im więcej szkół sprawdzamy, tym pewniej jakaś para
                  pogorszeń wypadnie razem bez żadnego powiązania. Dłuższe okno policzy tę samą
                  dwuznaczność dokładniej, ale jej nie usunie.
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ color: 'var(--uwaga)' }}>
                  Co by to rozstrzygnęło
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--tekst)' }}>
                  Interwencja, nie obserwacja. Wybrać szkoły z tej listy, LOSOWO przypisać część z nich do
                  zmiany działającej wyłącznie na punktualność (wcześniejszy start kursu, inna kolejność
                  szkół na trasie), resztę zostawić bez zmiany, z góry zapisać długość okna i jedną mierzoną
                  wielkość — i odczytać wynik raz, po zakończeniu. Bez losowego przydziału i bez grupy bez
                  zmiany kolejne miesiące pokażą to samo współwystępowanie.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* ── zestawienie po szkole albo jawnie nazwana blokada ─────────────────────────── */}
          {crossReady && join ? (
            <Box>
              <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                Przecięcie dwóch progów, nie ranking szkół
              </Typography>
              <PasSekcji wariant="cichy" tytul="Co wyszło z zestawienia" />

              <Typography variant="h5" sx={{ mb: 1 }}>
                Zestawienie po szkole
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '80ch' }}>
                Lista zawiera wyłącznie szkoły, w których w tym samym oknie sprzedaż spadła o co najmniej{' '}
                {DEFAULT_THRESHOLDS.minSalesDropPct}%, a punktualność pogorszyła się o co najmniej{' '}
                {DEFAULT_THRESHOLDS.minOnTimeDropPoints} p.p. Szkoła, w której pogorszyło się tylko jedno
                z dwojga, tu nie trafia — i nie jest to dowód, że u niej wszystko gra.
              </Typography>

              {crossReady.coincidences.length === 0 ? (
                /*
                 * „Nie znaleziono" i „nie było czego szukać" to dwie różne odpowiedzi — i przez
                 * długi czas ten ekran mylił je mimo tego komentarza.
                 *
                 * DEFEKT ZNALEZIONY NA PRODUKCJI (przebieg 32598781650). Podawaliśmy tu
                 * `join.matched`, czyli liczbę szkół dopasowanych w ZŁĄCZENIU. Silnik zbieżności
                 * odsiewa jednak szkoły jeszcze raz, PO złączeniu: bez porównania punktualności
                 * między oknami nie ma czego z czym zestawić (crossSystem.ts, warunek na null-owej
                 * delcie). Na produkcji `on_time_delta_points` było null dla WSZYSTKICH szkół,
                 * więc silnik nie przeanalizował ANI JEDNEJ — a ekran napisał: „Silnik
                 * przeanalizował 15 szkół i żadna nie przekroczyła progów istotności. To jest
                 * wynik". To zdanie było nieprawdziwe. To NIE był wynik; to był brak danych.
                 *
                 * Liczbą, którą silnik REALNIE zbadał, jest `population.analysed` — i tylko ona
                 * uprawnia do zdania „sprawdziliśmy i nic nie ma".
                 */
                <InsightOutcome
                  kartaId="CROSS_SYSTEM-17"
                  analysed={crossReady.population.analysed}
                  window={crossReady.window}
                  subject="szkół"
                  powodBrakuAnalizy={powodBrakuPorownania(crossReady.population)}
                />
              ) : (
                <Stack spacing={2}>
                  {crossReady.coincidences.map((item) => (
                    <KartaPowiazania
                      key={item.school_id}
                      item={item}
                      join={join}
                      okno={crossReady.window}
                      porownanie={crossReady.comparison}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="overline" sx={{ color: 'var(--krytyczny)' }}>
                Stan: blokada
              </Typography>
              <Typography variant="h5" sx={{ mb: 1.5 }}>
                {naglowekBlokady}
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 'var(--r-karta)',
                  borderColor: 'var(--krytyczny)',
                  background: 'var(--krytyczny-tlo)',
                }}
              >
                <Typography variant="body1" sx={{ color: 'var(--tekst)', maxWidth: '80ch' }}>
                  Zestawienie ma dwie strony i obie muszą istnieć. Tu nie istnieje przynajmniej jedna,
                  więc ekran nie pokazuje żadnej liczby zastępczej — pokazuje, czego brakuje.
                </Typography>

                {/*
                 * Cztery wiersze diagnostyczne + wiersz okna są w inwentarzu JEDNĄ kartą
                 * (CROSS_SYSTEM-23), a każdy z nich pojawia się warunkowo — adres siedzi więc
                 * na bloku, który je zbiera, bo tylko on istnieje w każdym wariancie blokady.
                 */}
                <Box data-cc-card-id="CROSS_SYSTEM-23" sx={{ mt: 2 }}>
                  <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                    Co blokuje zestawienie
                  </Typography>

                  {powodZHaka ? (
                    <Wiersz
                      etykieta="Powód zgłoszony przez warstwę danych"
                      wartosc={powodZHaka}
                      dopisek="komunikat cytowany dosłownie; pochodzi z warstwy danych Control Center, nie z odpowiedzi backendu"
                    />
                  ) : null}

                  {brakWyniku ? (
                    <Wiersz
                      etykieta="Wynik złączenia"
                      wartosc="nie zwrócony"
                      dopisek="hak nie podał ani wyniku, ani przyczyny — to stan nieokreślony, nie zero"
                    />
                  ) : null}

                  {!driveConnected ? (
                    <Wiersz
                      etykieta="Strona dostaw"
                      wartosc="Wymaga podłączenia systemu kierowców"
                      dopisek="osobny katalog kont i osobny sekret sesji — to widoczny koszt braku zunifikowanej tożsamości"
                    />
                  ) : null}

                  {sprzedazPusta ? (
                    <Wiersz
                      etykieta="Strona sprzedaży"
                      wartosc="pusty zbiór: 0 zamówień w 0 szkołach"
                      dopisek="bez sprzedaży nie ma czego z czym zestawiać — i nie jest to zmierzone zero sprzedaży, tylko brak wierszy do policzenia"
                    />
                  ) : null}

                  <Wiersz
                    etykieta="Okno, którego brak dotyczy"
                    wartosc={oknoTekst}
                    dopisek="brak podany bez okna jest nieweryfikowalny"
                  />
                </Box>

                {sprzedazPusta ? (
                  <Typography variant="body2" sx={{ mt: 2, color: 'var(--tekst-2)', maxWidth: '80ch' }}>
                    Bez sprzedaży nie ma czego z czym zestawiać. Nie wstawiamy w to miejsce zera ani żadnej
                    liczby zastępczej: zero znaczyłoby „zmierzyliśmy i nikt nic nie zamówił”, a prawda brzmi
                    „nie mamy w tym oknie ani jednego wiersza sprzedaży”.
                  </Typography>
                ) : null}

                {!driveConnected ? (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <Link to="/drive">Podłącz dostawy</Link>, żeby zestawienie dało się w ogóle policzyć.
                  </Typography>
                ) : null}
              </Paper>
            </Box>
          )}

          <Typography
            variant="caption"
            data-cc-card-id="CROSS_SYSTEM-24"
            sx={{ color: 'var(--tekst-slaby)', display: 'block' }}
          >
            Silnik: {CROSS_ENGINE_VERSION} · okno {oknoTekst}
            {crossReady ? ` · porównanie ${crossReady.comparison.date_from} → ${crossReady.comparison.date_to}` : ''} ·{' '}
            {describeDataOrigin(config.dataOrigin)} · sprzedaż i dostawy liczone są w dwóch osobnych
            systemach i łączone dopiero tutaj, po identyfikatorze szkoły
            {zablokowane ? ' · złączenia w tym oknie nie policzono' : ''}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}
